import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTE } from '@/constants';
import { ClientContainer, UserContainer } from '@/providers';
import { IPlan } from '@/components/subscription-change-modal/constant';
import { usePlanRecommendation } from '@/hooks/use-plan-recommendation';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { BILLING_ANALYTICS_EVENTS } from '@/analytics/billing';
import { trackEvent } from '@/helpers/external-tools-helper';
import { cn } from '@/lib/utils';
import teamUpgradeCover from '../../images/upgrade-modal-cover.svg';
import professionalCover from '../../images/upgrade-modal-professional.svg';
import teamLogo from '../../images/team-icon.svg';
import professionalLogo from '../../images/professional-icon.svg';

// Feature keys that can be bolded
type FeatureKey =
  | 'data-sources_other'
  | 'data-sources_unlimited'
  | 'workspace_other'
  | 'users_other'
  | 'analytics-dashboard'
  | 'google-add-on'
  | 'reporting';

// Define plan configurations
const PLAN_CONFIGS = {
  professional: {
    title: 'Professional',
    description: 'To grow referrals and leads of business looking',
    price: 39,
    features: [
      { key: 'data-sources_other', count: 10 },
      { key: 'workspace_other', count: 5 },
      { key: 'users_other', count: 5 },
      { key: 'analytics-dashboard' },
      { key: 'google-add-on' },
      { key: 'reporting' },
    ],
    icon: 'briefcase',
  },
  team: {
    title: 'Team',
    description: 'Perfect for growing teams and businesses',
    price: 99,
    features: [
      { key: 'data-sources_unlimited' },
      { key: 'workspace_other', count: 20 },
      { key: 'users_other', count: 20 },
      { key: 'analytics-dashboard' },
      { key: 'google-add-on' },
      { key: 'reporting' },
    ],
    icon: 'users',
  },
};

type ResourceType = 'datasources' | 'workspaces' | 'users';

interface TeamUpgradeDialogProps {
  open: boolean;
  onOpenChange: (openState: boolean) => void;
  resourceType?: ResourceType; // The resource that triggered the upgrade modal
  planName?: 'professional' | 'team'; // Optional override for plan (for backwards compatibility)
}

const TeamUpgradeDialog = ({
  open,
  onOpenChange,
  resourceType,
  planName: planNameProp,
}: TeamUpgradeDialogProps) => {
  const { t } = useTranslation('pricing');
  const navigate = useNavigate();
  const { state: clientState } = ClientContainer.useContainer();
  const { selectedClient } = clientState;

  // Use plan recommendation hook to determine the right plan based on requirements
  const { suggestedPlan, professionalPlan, teamPlan } = usePlanRecommendation();

  // Use suggested plan or fallback to prop for backwards compatibility
  const planName =
    planNameProp ||
    (suggestedPlan === 'contact_support' ? 'team' : suggestedPlan);

  const planConfig = PLAN_CONFIGS[planName];

  // Determine which features to bold based on resource type
  const getBoldFields = (): Partial<Record<FeatureKey, boolean>> => {
    if (!resourceType) return {};

    const boldFields: Partial<Record<FeatureKey, boolean>> = {};

    if (resourceType === 'datasources') {
      boldFields['data-sources_other'] = true;
      boldFields['data-sources_unlimited'] = true;
    } else if (resourceType === 'workspaces') {
      boldFields.workspace_other = true;
    } else if (resourceType === 'users') {
      boldFields.users_other = true;
    }

    return boldFields;
  };

  const boldFields = getBoldFields();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 gap-0">
        <DialogHeader className="p-6">
          <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
            <i className={`ti ti-${planConfig.icon} text-yellow-400`} />
            {t(
              planName === 'team'
                ? 'plan-team-upgrade-description'
                : 'plan-professional-upgrade-description'
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-6 border-t">
          <div className="space-y-6 p-6 pr-0 pb-4 ">
            <div>
              <img
                src={planName === 'team' ? teamLogo : professionalLogo}
                alt=""
              />
              <div className="text-xl font-semibold">{planConfig.title}</div>
              <div className="text-sm text-gray-500">
                {planConfig.description}
              </div>
            </div>

            <div>
              <div className="flex items-baseline">
                <span className="text-3xl font-bold">${planConfig.price}</span>
                <span className="text-gray-500 ml-1">/{t('month')}</span>
              </div>
              <div className="text-sm text-gray-500">{t('billed-yearly')}</div>
            </div>

            <div className="space-y-3">
              <div className="font-medium text-xs text-gray-500">
                {t('features')}
              </div>
              <ul className="space-y-3">
                {planConfig.features.map(feature => (
                  <li
                    key={feature.key}
                    className="flex items-center gap-2 text-xs text-gray-500"
                  >
                    <i className="ti ti-check text-blue-500" />
                    <span
                      className={cn(
                        boldFields?.[feature.key]
                          ? 'font-semibold text-black'
                          : 'font-normal'
                      )}
                    >
                      {feature.count
                        ? t(feature.key, { count: feature.count })
                        : t(feature.key)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="w-auto h-auto overflow-hidden">
            <img
              src={planName === 'team' ? teamUpgradeCover : professionalCover}
              alt={planConfig.title}
              className="w-[500px] scale-110"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t">
          <Button intent="secondary" onClick={() => onOpenChange(false)}>
            {t('action_cancel')}
          </Button>
          <Button
            startIcon={<i className="ti ti-bolt" />}
            onClick={() => {
              trackEvent(
                BILLING_ANALYTICS_EVENTS.SUBSCRIPTION_UPGRADE_CHECKED,
                {
                  planName,
                }
              );
              navigate(
                `/ws/${selectedClient?.id}/${APP_ROUTE.SETTINGS}/${APP_ROUTE.BILLING}?plan=${planName}`
              );
              onOpenChange(false);
            }}
          >
            {t('button-view-all-plans', 'View All Plans')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TeamUpgradeDialog;
